/**
 * FunnelStageRow — card vertical de etapa do funil.
 */
import React from 'react';
import { AppIcon } from '../../../../components/icons/AppIcon';
import { Button } from '../../../../components/ui/Button';
import Input from '../../../../components/ui/Input';
import Select, { type SelectOption } from '../../../../components/ui/Select';
import Switch from '../../../../components/ui/Switch';
import { cn } from '@/lib/utils';

const CONVERSION_EVENTS: SelectOption[] = [
    { value: '', label: 'Nenhum' },
    { value: 'Lead', label: 'Lead' },
    { value: 'Contact', label: 'Contato' },
    { value: 'Schedule', label: 'Agendamento' },
    { value: 'SubmitApplication', label: 'Candidatura' },
    { value: 'Purchase', label: 'Compra' },
    { value: 'CompleteRegistration', label: 'Cadastro completo' },
    { value: 'AddToCart', label: 'Adicionar ao carrinho' },
    { value: 'InitiateCheckout', label: 'Iniciar checkout' },
];

const STAGE_NUMBER_TONES = [
    { backgroundColor: 'var(--info-bg)', color: 'var(--info-600)' },
    { backgroundColor: 'var(--warning-bg)', color: 'var(--warning-600)' },
    { backgroundColor: 'var(--brand-50)', color: 'var(--brand-900)' },
    { backgroundColor: 'var(--success-bg)', color: 'var(--success-600)' },
] as const;

const META_CHIP = 'inline-flex items-center gap-s1';

/** Tom do badge numérico da etapa (usado no onboarding). */
export function stageNumberTone(index: number, stageColor?: string | null) {
    if (stageColor) {
        return {
            backgroundColor: `color-mix(in srgb, ${stageColor} 16%, white)`,
            color: stageColor,
        };
    }
    return STAGE_NUMBER_TONES[index % STAGE_NUMBER_TONES.length];
}

function conversionEventLabel(value: string): string {
    return CONVERSION_EVENTS.find((event) => event.value === value)?.label ?? 'Nenhum';
}

export interface FunnelStageData {
    id?: string;
    name: string;
    deadline?: number;
    conversionEvent: string;
    color?: string | null;
}

interface FunnelStageRowProps {
    stage: FunnelStageData;
    index: number;
    isDragging: boolean;
    showConversionEvent?: boolean;
    onNameChange: (value: string) => void;
    onDeadlineChange: (value: number) => void;
    onConversionEventChange?: (value: string) => void;
    onRemove: () => void | Promise<void>;
    onDragStart: () => void;
    onDragOver: (e: React.DragEvent) => void;
    onDragEnd: () => void;
}

const FunnelStageRow: React.FC<FunnelStageRowProps> = ({
    stage,
    index,
    isDragging,
    showConversionEvent = true,
    onNameChange,
    onDeadlineChange,
    onConversionEventChange,
    onRemove,
    onDragStart,
    onDragOver,
    onDragEnd,
}) => {
    const step = index + 1;
    const stageKey = stage.id ?? index;
    const stagnating = Boolean(stage.deadline && stage.deadline > 0);
    const displayName = stage.name.trim() || `Etapa ${step}`;
    const showMeta = showConversionEvent || stagnating;

    return (
        <article
            className={cn(
                'card flex w-[min(100vw-2rem,18rem)] shrink-0 flex-col overflow-hidden bg-card transition-[border-color,box-shadow] duration-150 sm:w-[18rem]',
                isDragging
                    ? 'opacity-50 ring-2 ring-primary/40'
                    : 'hover:border-primary/25 hover:shadow-sm',
            )}
            draggable
            onDragStart={onDragStart}
            onDragOver={onDragOver}
            onDragEnd={onDragEnd}
            aria-label={`Etapa ${step}: ${displayName}`}
        >
            <header className="flex items-start justify-between gap-s2 border-b border-subtle px-s4 pt-s4 pb-s3">
                <div className="min-w-0 flex-1">
                    <h3 className="card-title mb-0 truncate">{displayName}</h3>
                    {showMeta && (
                        <p className="card-sub mt-s1 flex flex-wrap items-center gap-x-s3 gap-y-s1">
                            {showConversionEvent && (
                                <span className={META_CHIP}>
                                    <AppIcon name="chart" className="size-3.5 shrink-0" aria-hidden />
                                    <span className="truncate">
                                        {conversionEventLabel(stage.conversionEvent)}
                                    </span>
                                </span>
                            )}
                            {stagnating && (
                                <span className={cn(META_CHIP, 'font-mono tabular-nums')}>
                                    <AppIcon name="bell" className="size-3.5 shrink-0" aria-hidden />
                                    <span>
                                        {stage.deadline} {stage.deadline === 1 ? 'dia' : 'dias'}
                                    </span>
                                </span>
                            )}
                        </p>
                    )}
                </div>
                <span
                    className="flex size-8 shrink-0 cursor-grab items-center justify-center rounded-md text-muted transition-colors hover:bg-surface-subtle active:cursor-grabbing"
                    aria-hidden
                >
                    <AppIcon name="grip-vertical" className="size-4" />
                </span>
            </header>

            <div className="flex flex-1 flex-col gap-s4 px-s4 py-s4">
                <Input
                    id={`stage-name-${stageKey}`}
                    label="Nome"
                    value={stage.name}
                    onChange={(e) => onNameChange(e.target.value)}
                    placeholder="Nome da etapa"
                />

                {showConversionEvent && onConversionEventChange && (
                    <Select
                        id={`stage-event-${stageKey}`}
                        label="Evento do Meta"
                        value={stage.conversionEvent}
                        onChange={(e) => onConversionEventChange(e.target.value)}
                        options={CONVERSION_EVENTS}
                    />
                )}

                <div className="flex flex-col gap-s2">
                    <Switch
                        id={`stage-stagnate-${stageKey}`}
                        checked={stagnating}
                        onChange={(checked) =>
                            onDeadlineChange(checked ? Math.max(stage.deadline || 1, 1) : 0)
                        }
                        label="Estagnando em (dias)"
                    />
                    {stagnating && (
                        <Input
                            id={`stage-deadline-${stageKey}`}
                            type="number"
                            min={1}
                            inputMode="numeric"
                            aria-label="Dias até estagnar"
                            value={stage.deadline && stage.deadline > 0 ? stage.deadline : ''}
                            onChange={(e) => {
                                const next = e.target.value === '' ? 0 : parseInt(e.target.value, 10);
                                onDeadlineChange(Number.isNaN(next) ? 0 : next);
                            }}
                            placeholder="Dias"
                        />
                    )}
                </div>
            </div>

            <footer className="mt-auto border-t border-subtle px-s4 py-s3">
                <Button
                    type="button"
                    variant="ghost"
                    onClick={() => void onRemove()}
                    className="min-h-9 touch-manipulation px-0 text-muted hover:bg-transparent hover:text-destructive"
                >
                    <AppIcon name="trash" className="size-4" aria-hidden />
                    Excluir etapa
                </Button>
            </footer>
        </article>
    );
};

export default FunnelStageRow;
